export const ADMIN_DATE_PLACEHOLDER = '—';

const PROFILE_FIELD_KEYS = [
  'companyName',
  'personalPhone',
  'phone',
  'language',
  'onboardingCompleted',
  'requestedPersona',
  'personaRequestedAt',
  'personaReviewedAt',
  'personaRejectionReason',
];

const DATE_PROFILE_KEYS = new Set(['personaRequestedAt', 'personaReviewedAt']);

/**
 * @param {string | null | undefined} role
 * @param {(key: string) => string} t
 * @returns {string}
 */
export function adminRoleLabel(role, t) {
  if (!role) return t('admin.users.role.none');
  const key = `admin.settings.role.${role}`;
  const label = t(key);
  return label === key ? role : label;
}

/**
 * @param {unknown} value
 * @param {string} [language]
 * @returns {string}
 */
export function formatAdminDate(value, language = 'fr') {
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return ADMIN_DATE_PLACEHOLDER;
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return ADMIN_DATE_PLACEHOLDER;
  }
}

/**
 * @param {unknown} value
 */
export function hasUsefulProfileValue(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'boolean' || typeof value === 'number') return true;
  return false;
}

/**
 * @param {object | null | undefined} profile
 * @returns {string}
 */
export function formatAdminAddress(profile) {
  if (!profile || typeof profile !== 'object') return '';
  return [
    profile.personalAddressStreet,
    profile.personalAddressCity,
    profile.personalAddressProvince,
    profile.personalAddressPostalCode,
    profile.personalAddressCountry,
  ]
    .filter((part) => typeof part === 'string' && part.trim())
    .join(', ');
}

/**
 * Useful, non-empty profile rows actually returned by getAdminUser.
 * @param {object | null | undefined} profile
 * @returns {{ key: string, value: unknown, kind: 'text' | 'boolean' | 'date' | 'persona' }[]}
 */
export function visibleAdminProfileFields(profile) {
  if (!profile || typeof profile !== 'object') return [];
  const rows = [];
  for (const key of PROFILE_FIELD_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(profile, key)) continue;
    const value = profile[key];
    if (!hasUsefulProfileValue(value)) continue;
    let kind = 'text';
    if (key === 'onboardingCompleted') kind = 'boolean';
    else if (key === 'requestedPersona') kind = 'persona';
    else if (DATE_PROFILE_KEYS.has(key)) kind = 'date';
    rows.push({ key, value, kind });
  }
  return rows;
}

/**
 * @param {unknown} error
 * @returns {'not-found' | 'unavailable' | 'internal' | 'permission-denied' | 'error'}
 */
export function classifyAdminCallableError(error) {
  const raw = String(error?.code || '').trim().toLowerCase();
  const code = raw.startsWith('functions/') ? raw.slice('functions/'.length) : raw;
  if (code === 'not-found') return 'not-found';
  if (code === 'unavailable') return 'unavailable';
  if (code === 'internal') return 'internal';
  if (code === 'permission-denied') return 'permission-denied';
  if (code === 'failed-precondition') return 'failed-precondition';
  return 'error';
}
